import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { InputObservable, connectState } from '@y42/angular/util';
import { FeatureAggregationService } from '@y42/feature';
import { ImportWriteType } from '@y42/job';
import { DynamicOrchestrationConfig, StaticOrchestrationConfig } from '@y42/json-schema';
import {
  OrchestrationRunJobDetailsStoreModel,
  OrchestrationRunService,
  OrchestrationRunUIModel,
  TriggerOrchestrationLocationPayload,
} from '@y42/orchestration';
import { OrgUserQuery } from '@y42/org-user/state';
import { showLoaderFor } from '@y42/util/show-loader-for-x-ms';
import { BehaviorSubject, Observable, distinctUntilChanged, map, of, shareReplay, switchMap, tap } from 'rxjs';
import {
  OrchestrationDiagramConfig,
  OrchestrationNodeBindings,
  OrchestrationUINode,
} from '../../orchestration-diagram/models/orchestration-diagram.model';
import { OrchestrationScheduleType } from '../../orchestration-run-name/orchestration-run-name/orchestration-run-name.component';
import {
  OrchestrationTriggeredBy,
  OrchestrationTriggeredByOrigin,
} from '../../orchestration-triggered-by/orchestration-triggered-by/orchestration-triggered-by.component';
import { OrchestrationRunDetails } from '../orchestration-run-details-stripe/orchestration-run-details-stripe.component';

@UntilDestroy()
@Component({
  selector: 'y42-orchestration-run-details[runId]',
  templateUrl: './orchestration-run-details.component.html',
  styleUrls: ['./orchestration-run-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationRunDetailsComponent {
  constructor(
    private readonly orchestrationRunService: OrchestrationRunService,
    private readonly orgUserQuery: OrgUserQuery,
    private readonly featureAggregationService: FeatureAggregationService,
  ) {}

  @Input()
  @InputObservable()
  runId!: string;
  runId$!: Observable<string | undefined>;

  @Output() redirectToAsset = new EventEmitter<string>();

  private readonly dataLoading$$ = new BehaviorSubject(true);

  private readonly runUiModel$ = this.runId$.pipe(
    distinctUntilChanged(),
    tap(() => this.dataLoading$$.next(true)),
    switchMap((runId) => (runId ? this.orchestrationRunService.getOrchestrationRun(runId) : of(undefined))),
    tap(() => this.dataLoading$$.next(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly state = connectState(this, {
    dataLoading: this.dataLoading$$.pipe(showLoaderFor(500)),
    runDetails: this.runUiModel$.pipe(
      map((run) => {
        if (!run) {
          return undefined;
        }

        const runDetails: OrchestrationRunDetails = {
          runStatus: run.status,
          startedAt: new Date(run.created_at),
          finishedAt: run.finished_at ? new Date(run.finished_at) : undefined,
          runName:
            run.triggered_by.mode === 'adhoc'
              ? { scheduleType: OrchestrationScheduleType.AdHoc }
              : {
                  scheduleType: OrchestrationScheduleType.Scheduled,
                  scheduledPath:
                    (run.payload as TriggerOrchestrationLocationPayload)?.location || 'Unknown orchestration',
                },
          runId: run.run_id,
          triggeredBy: this.getRunTriggeredBy(run),
        };

        return runDetails;
      }),
    ),
    diagramConfig: this.runUiModel$.pipe(
      map((run) => {
        if (!run) {
          return undefined;
        }

        const nodes = run.details.nodes;
        const linkDataArray = run.details.links;
        const nodeDataArray = [];

        for (const key in nodes) {
          const node = nodes[key];
          const nodeData: OrchestrationUINode = {
            key,
            path: node.path,
            entityType: node.entity_type || '',
            [OrchestrationNodeBindings.JobId]: node.job_id ?? undefined,
            [OrchestrationNodeBindings.JobStatus]: node.job_status,
          };
          nodeDataArray.push(nodeData);
        }

        const diagramConfig: OrchestrationDiagramConfig = {
          mode: 'view',
          model: {
            linkDataArray,
            nodeDataArray,
            visualDataArray: [],
          },
        };

        return diagramConfig;
      }),
    ),
    runJobs: this.runUiModel$.pipe(
      map((run) => {
        if (!run) {
          return [];
        }

        return Object.keys(run.details.nodes).map((key) => {
          const node = run.details.nodes[key];
          const assetPath = node.path;
          const serviceDef = this.featureAggregationService.getServiceDefinitionByPath(assetPath);

          let isIncremental = false;
          if (run) {
            const runConfigDynamic = run.run_config as DynamicOrchestrationConfig;
            const isIncrementalDynamic = !!runConfigDynamic?.incremental;

            const runConfigStatic = run.run_config as StaticOrchestrationConfig;
            const isIncrementalStatic = !!(runConfigStatic?.nodeDataArray || []).find((n) => n.key === key)?.nodeConfig
              ?.incremental;

            isIncremental = isIncrementalDynamic || isIncrementalStatic;
          }

          const nodePathToRunNode: Record<string, OrchestrationRunJobDetailsStoreModel> = {};
          const allNodes = run?.details.nodes || {};
          for (const key in allNodes) {
            const nodeUnderKey = allNodes[key];
            nodePathToRunNode[nodeUnderKey.path] = nodeUnderKey;
          }

          const currentAsset = nodePathToRunNode[assetPath];

          return {
            featureType: serviceDef!.featureType,
            assetPath,
            mode: isIncremental ? ImportWriteType.Append : ImportWriteType.Truncate,
            triggeredBy: this.getRunTriggeredBy(run),
            jobStatus: currentAsset.job_status,
            jobId: currentAsset.job_id || '-',
            createdAt: currentAsset.created_at,
            finishedAt: currentAsset.finished_at,
            totalRowCount: currentAsset.details.all_rows,
            currentRowCount: currentAsset.details.new_rows,
          };
        });
      }),
    ),
  });

  private getRunTriggeredBy(run: OrchestrationRunUIModel | undefined) {
    const getUserTriggeredBy = () => {
      if (!run?.triggered_by.user_id) {
        return undefined;
      }
      return this.orgUserQuery.getUIEntity(run.triggered_by.user_id);
    };

    const triggeredBy: OrchestrationTriggeredBy =
      run?.triggered_by.type === 'user'
        ? {
            origin: OrchestrationTriggeredByOrigin.User,
            user: getUserTriggeredBy(),
          }
        : {
            origin: OrchestrationTriggeredByOrigin.Orchestrator,
          };
    return triggeredBy;
  }
}
